/**
 * Helper functions for product update operations
 */

/**
 * Check if FormData contains any files
 */
export function hasFiles (formData: FormData): boolean {
  for (const value of formData.values()) {
    if (value instanceof File) {
      return true;
    }
  }
  return false;
}

/**
 * Build JSON body from FormData entries
 * Handles array fields (ending with []) and type coercion
 */
export function buildJsonBodyFromFormData (formData: FormData): Record<string, unknown> {
  const jsonBody: Record<string, unknown> = {};
  const arrays: Record<string, unknown[]> = {};

  formData.forEach((raw, key) => {
    // Skip files - they're handled separately
    if (raw instanceof File) {
      return;
    }

    const val = typeof raw === 'string' ? raw : (raw as unknown);

    // Handle array fields (ending with [])
    if (key.endsWith('[]')) {
      const base = key.slice(0, -2);
      if (!arrays[base]) {
        arrays[base] = [];
      }
      arrays[base].push(val);
      return;
    }

    jsonBody[key] = val;
  });

  // Merge arrays into jsonBody
  Object.keys(arrays).forEach(k => {
    jsonBody[k] = arrays[k];
  });

  // Coerce known fields to proper types
  if (jsonBody.type_id != null) {
    jsonBody.type_id = Number(jsonBody.type_id);
  }
  if (jsonBody.brand_id != null) {
    jsonBody.brand_id = Number(jsonBody.brand_id);
  }
  if (jsonBody.price != null) {
    jsonBody.price = Number(jsonBody.price);
  }
  if (jsonBody.has_serial != null) {
    jsonBody.has_serial = String(jsonBody.has_serial) === 'true';
  }

  return jsonBody;
}

/**
 * Build FormData for multipart upload with flat fields and image files.
 *
 * The backend controller reads scalar fields from req.body (flat multipart
 * fields), NOT from a nested JSON blob. Each field is appended individually
 * so Express/Multer can parse them from req.body as usual.
 */
export function buildFormDataWithJsonAndFiles (formData: FormData): FormData {
  const fd = new FormData();
  const jsonBody = buildJsonBodyFromFormData(formData);
  const imageFiles: File[] = [];

  // Extract image files separately
  formData.forEach((raw, key) => {
    if (raw instanceof File && key === 'images') {
      imageFiles.push(raw);
    }
  });

  // Append each scalar field as a flat multipart entry
  Object.entries(jsonBody).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      (value as unknown[]).forEach(v => fd.append(`${key}[]`, String(v)));
    } else {
      fd.append(key, String(value));
    }
  });

  // Append image files
  imageFiles.forEach(file => {
    fd.append('images', file);
  });

  return fd;
}

